import React, { useState, useEffect, useRef, useCallback } from "react";
import Section from "../../Layouts/Section";
import { useGemini } from "./useGemini";
import { marked } from "marked";
const Assistant = () => {
  const [question, setQuestion] = useState("");
  const [chatHistory, setChatHistory] = useState<
    { question: string; answer: string }[]
  >(() => {
    const savedHistory = localStorage.getItem("chatHistory");
    return savedHistory
      ? JSON.parse(savedHistory)
      : [
          {
            question: "",
            answer: "How can I help you? Feel free to use any language! 😊",
          },
        ];
  });
  const { ask, loading } = useGemini();
  const chatHistoryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem("chatHistory", JSON.stringify(chatHistory));
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTo({
        top: chatHistoryRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [chatHistory]);

  const handleAsk = useCallback(async () => {
    const _question = question.trim();
    if (!_question) return;
    const prompt = `You are Duy Nha, a professional with the following background:

    ${JSON.stringify(aboutMe, null, 2)}
    
    Respond to the question below as if you are Duy Nha. Ensure your answer is:
    - Polite, coherent, and friendly 😊.
    - Uses the correct language based on the question.
    - Provides concise and relevant information.
    - Uses emojis if appropriate.
    
    **Question:** ${_question}
    
    **Your Answer:**`;
    setQuestion("");
    setChatHistory((prev) => [
      ...prev,
      { question: _question, answer: "Answering..." },
    ]);

    const { answer } = await ask(prompt);

    const formattedAnswer = await marked(answer.replace(/\n/g, "<br/>"));

    setChatHistory((prev) => [
      ...prev.slice(0, -1),
      { question: _question, answer: formattedAnswer },
    ]);
  }, [ask, question]);

  useEffect(() => {
    const handleEnterKey = (event: KeyboardEvent) => {
      if (event.key === "Enter" && !loading) {
        void handleAsk();
      }
    };

    window.addEventListener("keydown", handleEnterKey);

    return () => {
      window.removeEventListener("keydown", handleEnterKey);
    };
  }, [handleAsk, loading]);

  const clearChatHistory = () => {
    setChatHistory([
      {
        question: "",
        answer: "How can I help you? Feel free to use any language! 😊",
      },
    ]);
  };

  return (
    <Section
      title="Assistant"
      className="text-sm"
      id="assistantId"
      leftChildren={
        chatHistory.length > 1 && (
          <button
            onClick={clearChatHistory}
            className="bg-red-500 text-white p-2 rounded"
          >
            Clear Chat History
          </button>
        )
      }
    >
      <div className="flex flex-col gap-4  ">
        <div
          ref={chatHistoryRef}
          className="chat-history max-h-[60vh] overflow-y-auto  custom-scrollbar p-4"
        >
          {chatHistory.map((chat, index) => (
            <div key={index} className="chat-message mb-4 ">
              {chat.question && (
                <p className="text-right mb-4 text-s p-2 rounded-2xl w-fit max-w-[75%] ml-auto bg-[#2563eb] dark:bg-[#1e40af] text-white">
                  {chat.question}
                </p>
              )}
              <div className="text-left flex flex-row items-end gap-1 ">
                <div className="w-9 h-9 flex justify-center  items-center p-2 rounded-full bg-[#e2e8f0] dark:bg-[#1e293b] text-[#0f172a] dark:text-[#f8fafc]">
                  <b>N</b>
                </div>
                <p className="text-s p-2 rounded-2xl w-fit max-w-[75%] bg-[#e2e8f0] dark:bg-[#1e293b] text-[#0f172a] dark:text-[#f8fafc]">
                  <span dangerouslySetInnerHTML={{ __html: chat.answer }} />
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 pl-1 ">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            className=" bg-[#f1f5f9] border-[#cbd5e1] text-[#0f172a] placeholder:text-[#0f172a] border px-3 py-4 focus:border-[#3b82f6] flex-1 rounded-lg dark:text-[#f8fafc] dark:bg-[#1e293b] dark:border-[#334155] dark:placeholder:text-[#94a3b8]"
            placeholder="Ask me anything..."
            disabled={loading}
          />
          <button
            onClick={handleAsk}
            className="bg-[#2563eb] text-white hover:bg-[#1d4ed8] active:bg-[#1e40af] px-4 py-2 rounded duration-100 dark:bg-[#3b82f6] dark:hover:bg-[#2563eb] dark:active:bg-[#1e40af] dark:disabled:bg-gray-500 dark:disabled:hover:bg-gray-500 dark:disabled:text-white disabled:bg-gray-500 disabled:hover:bg-gray-500 disabled:text-white disabled:hover:cursor-not-allowed"
            disabled={loading || !question.trim()}
          >
            {loading ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </Section>
  );
};

export default Assistant;

const aboutMe = {
  name: "Tran Duy Nha",
  age: "24",
  dateOfBirth: "20/01/2001",
  profession: "Software Engineer",
  experience: {
    total_years: "3",
    current_company: {
      name: "Nexon Dev Vina",
      position: "HTML/CSS/JS Developer",
      duration: "September 2024 - Present",
      projects: [
        {
          name: "Crazy Arcade",
          description: "Short play of the crazy arcade to introduce the game.",
          tech_stack: ["Phaser", "TypeScript"],
          responsibilities: ["Create class of the game", "Handle logic game"],
        },
        {
          name: "Mabinogi playlist",
          description: "Just a media =))",
          tech_stack: ["React", "TypeScript", "Zustand", "Framer Motion"],
          responsibilities: [
            "Handle state of current track using Zustand",
            "Create UI base on figma design",
            "Handle animation using Framer Motion",
          ],
        },
        {
          name: "More than but i can not describe",
          description: "More than but i can not describe",
        },
      ],
    },
    previous_companies: [
      {
        name: "DISION TECH",
        position: "Frontend Engineer",
        duration: "Jul 2023 - Sep 2024",
        projects: [{}],
      },
      {
        name: "VNG Games",
        position: "Software Engineer",
        duration: "Nov 2022 - August 2024",
        projects: [
          {
            name: "GE Portal",

            description:
              " GE Portal uses micro-frontend architecture, used for managing centrally many mini apps such as Aurora, Ticket,... each mini app is one micro and it helps our BU reduce the time of authentication, move between applications, and more.",
            tech_stack: [
              "React",
              "TypeScript",
              "Single SPA",
              "AntDesign",
              "ReactContext",
            ],
            responsibilities: [
              "Using Micro FrontEnd Architecture with Single SPA Framework.",
              "Developed the UI, responsive design, and logic for the application.",
            ],
          },
          {
            name: "Aurora",
            description:
              "Game testers conduct surveys to evaluate a game on this platform. The results of surveys will be collected and featured on charts for analyzing and forecasting the game.",
            tech_stack: [
              "React",
              "TypeScript",
              "React Context",
              "Jotai",
              "ReactQuery/TanStackQuery",
              "AntDesign",
              "ECharts",
            ],
            responsibilities: [
              "Using Micro FrontEnd Architecture with Single SPA Framework.",
              "Developed the UI, responsive design, and logic for the application.",
              "Using Echart to visualize data.",
              "Config settings micro app of micro-frontend adapt with GE Portal.",
              "Implement UI based on Figma using ReactJS - TypeScript - AntDesign.",
              "Connect the backend with ReactQuery and Axios.",
              "Authenticate SSO with Microsoft.",
              "Handle state, and data flow using Reacthook and Jotai.",
              "Config Prettier and Eslint to handle code quality rules and stylistic rules.",
              "Break down requirements, plan, and estimate user stories.",
            ],
          },
          {
            name: "GE Talent",
            tech_stack: [
              "React",
              "TypeScript",
              "React Context",
              "Jotai",
              "ReactQuery/TanStackQuery",
              "AntDesign",
              "ECharts",
            ],
            description:
              "The GE Human Resources department wants to manage and track talented people who are at a high level and have outstanding performance. The tool is built based on existing HR databases.",
            responsibilities: [
              "Using Echart to visualize data.",
              "Implement UI based on Figma using ReactJS - TypeScript - AntDesign.",
              "Connect the backend with ReactQuery and Axios.",
              "Authenticate SSO with Microsoft.",
              "Handle state, and data flow using Reacthook and Jotai.",
              "Config Prettier and Eslint to handle code quality rules and stylistic rules.",
              "Break down requirements, plan, and estimate user stories.",
            ],
          },
        ],
      },
      {
        name: "USUM Software",
        position: "Front Engineer",
        duration: "Jun 2022 - Nov  2024",
        projects: [
          {
            name: "Gogobei",
          },
          {
            name: "Dakoli",
          },
        ],
      },
    ],
  },
  education: {
    university: "HCMC University of Technology and Education",
    major: "Information Technology",
    degree: "Bachelor's degree",
    gpa: "GPA 3.31/4 Very Good",
    scholarships: [
      {
        name: "Encouraging Study Scholarship 2022",
        description:
          "Awarded to the Top 10 students with the highest academic performance of the year in the cohort",
      },
    ],
  },
  myTechSkill: [
    "JavaScript",
    "TypeScript",
    "HTML",
    "CSS",
    "Java",
    "ReactJS",
    "Redux",
    "Jotai",
    "Tailwind CSS",
    "ExpressJS",
    "Single SPA",
    "EChart",
    "Axios",
    "NodeJs",
    "MySQL",
    "MongoDb",
    "Supabase",
    "ReactNative",
    "Oauth facebook",
    "google",
    "apple",
    "Webpack",
    "Vite",
    "GitLab",
    "Jenkins",
    "Docker",
    "Jira",
    "Confluence",
    "Windows",
    "MacOS",
    "Agile methodologies",
    "Problem-solving and troubleshooting",
    "Communication and Collaboration",
  ],
};
